import { queryOptions, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DbDestination {
  id: string;
  slug: string;
  name: string;
  summary: string | null;
  hero_image_url: string | null;
  lat: number | null;
  lng: number | null;
  wave_types: string[] | null;
  skill_levels: string[] | null;
  best_months: string[] | null;
  vibe_tags: string[] | null;
  crowd_level: string | null;
  budget_tier: string | null;
  swell_consistency: number | null;
  is_featured: boolean | null;
  adventure_score: number | null;
  social_scene_score: number | null;
  travel_ease_score: number | null;
  party_scene: boolean | null;
  remote_feel: boolean | null;
  has_surf_camps: boolean | null;
  has_surf_hostels: boolean | null;
  countries: { name: string } | null;
  continents: { name: string } | null;
}

export type WaveType = 'beach' | 'reef' | 'point';
export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';
export type CrowdLevel = 'empty' | 'quiet' | 'moderate' | 'busy' | 'crowded';

export interface UiDestination {
  id: string; // slug — used for routing
  name: string;
  country: string;
  continent: string;
  image: string;
  lat: number | null;
  lng: number | null;
  waveType: WaveType;
  skillLevel: SkillLevel;
  skillLevels: SkillLevel[];
  skillLabel: string;
  crowdLevel: CrowdLevel;
  costRating: number;
  consistency: number;
  bestMonths: string[];
  vibeTags: string[];
  description: string;
  /** 1-10, null if not yet researched for this destination -- callers must
   * not assume a fallback value; that's a judgement call for the consumer
   * (e.g. recommendation-reasons.ts degrading a rule to no-op). */
  adventureScore: number | null;
  socialSceneScore: number | null;
  travelEaseScore: number | null;
  /** True only when explicitly tagged -- null/false both mean "not confirmed
   * as this vibe," never inferred from the absence of data. */
  partyScene: boolean;
  remoteFeel: boolean;
  hasSurfCamps: boolean;
  hasSurfHostels: boolean;
}

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1200&q=80';

const normaliseWaveType = (waves: string[] | null): WaveType => {
  if (!waves?.length) return 'beach';
  const joined = waves.join(' ').toLowerCase();
  if (joined.includes('point')) return 'point';
  if (joined.includes('reef')) return 'reef';
  return 'beach';
};

const SKILL_ORDER: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const normaliseSkillLevels = (skills: string[] | null): SkillLevel[] => {
  if (!skills?.length) return ['intermediate'];
  const lower = skills.map((s) => s.toLowerCase());
  const found = SKILL_ORDER.filter((lvl) => lower.includes(lvl));
  return found.length ? found : ['intermediate'];
};

const skillLabelFromLevels = (levels: SkillLevel[]): string => {
  if (levels.length === 1) return cap(levels[0]);
  if (levels.length === 2) return `${cap(levels[0])} and ${cap(levels[1])}`;
  return `${cap(levels[0])} to ${cap(levels[levels.length - 1])}`;
};

const normaliseCrowd = (val: string | null): CrowdLevel => {
  switch ((val ?? '').toLowerCase()) {
    case 'low':
      return 'quiet';
    case 'high':
      return 'busy';
    case 'very high':
    case 'crowded':
      return 'crowded';
    case 'empty':
      return 'empty';
    default:
      return 'moderate';
  }
};

const normaliseCost = (val: string | null): number => {
  if (!val) return 2;
  const dollars = (val.match(/\$/g) || []).length;
  return dollars || 2;
};

export const toUiDestination = (row: DbDestination): UiDestination => {
  const skillLevels = normaliseSkillLevels(row.skill_levels);
  return {
    id: row.slug,
    name: row.name,
    country: row.countries?.name ?? '—',
    continent: row.continents?.name ?? '—',
    image: row.hero_image_url || FALLBACK_IMAGE,
    lat: row.lat,
    lng: row.lng,
    waveType: normaliseWaveType(row.wave_types),
    skillLevel: skillLevels[skillLevels.length - 1],
    skillLevels,
    skillLabel: skillLabelFromLevels(skillLevels),
    crowdLevel: normaliseCrowd(row.crowd_level),
    costRating: normaliseCost(row.budget_tier),
    consistency: row.swell_consistency ?? 5,
    bestMonths: row.best_months ?? [],
    vibeTags: row.vibe_tags ?? [],
    description: row.summary ?? '',
    adventureScore: row.adventure_score,
    socialSceneScore: row.social_scene_score,
    travelEaseScore: row.travel_ease_score,
    partyScene: row.party_scene ?? false,
    remoteFeel: row.remote_feel ?? false,
    hasSurfCamps: row.has_surf_camps ?? false,
    hasSurfHostels: row.has_surf_hostels ?? false,
  };
};

// Extracted so any imperative caller (TripPlannerWizard's
// fetchRecommendationPairs, via queryClient.ensureQueryData) shares the exact
// same query key, fetch logic, staleTime, and retry/retryDelay as the hook
// below -- one source of truth, so an imperative read can never silently
// diverge from what useDestinations() itself would return or how hard it
// retries before giving up.
export const destinationsQueryOptions = queryOptions({
  queryKey: ['destinations'],
  // The full destination list only changes via admin edits, not user
  // action -- safe to treat as fresh for longer than the app-wide
  // default. This is the single most-fetched query in the app (globe,
  // homepage, listing, detail, and trip planner all use it).
  staleTime: 5 * 60 * 1000,
  queryFn: async (): Promise<UiDestination[]> => {
    const { data, error } = await supabase
      .from('destinations')
      .select(
        'id,slug,name,summary,hero_image_url,lat,lng,wave_types,skill_levels,best_months,vibe_tags,crowd_level,budget_tier,swell_consistency,is_featured,adventure_score,social_scene_score,travel_ease_score,party_scene,remote_feel,has_surf_camps,has_surf_hostels,countries(name),continents(name)'
      )
      .eq('is_active', true)
      .order('is_featured', { ascending: false })
      .order('name', { ascending: true });

    if (error) {
      // Real error goes to console for debugging; callers must not render
      // this directly — see getFriendlyErrorMessage in src/lib/errors.ts
      // (raw PostgREST messages were leaking to the UI).
      console.error('Failed to load destinations:', error);
      throw error;
    }
    return (data as unknown as DbDestination[]).map(toUiDestination);
  },
  // Scoped override for this call site only — the global QueryClient
  // default (3 retries, exponential backoff up to 30s) meant this
  // central, above-the-fold query left users staring at a spinner for
  // ~9s before ever showing an error. One quick retry is
  // enough to ride out a transient blip without the long wait.
  retry: 1,
  retryDelay: 1000,
});

export const useDestinations = () => {
  return useQuery(destinationsQueryOptions);
};
